import { useEffect, useState, useRef } from "react";
import { Globe, Check } from "lucide-react";

const LANGS = [
  { code: "es", flag: "🇪🇸", label: "Español" },
  { code: "en", flag: "🇺🇸", label: "English" },
  { code: "pt", flag: "🇧🇷", label: "Português" },
];

// Set Google Translate language by writing the right cookie + finding select element.
function applyLanguage(code) {
  // Cookie path: works for current domain + path "/"
  const host = window.location.hostname;
  const cookie = code === "es" ? "" : `/es/${code}`;
  const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
  // Set on multiple scopes to be safe
  document.cookie = `googtrans=${cookie};path=/;expires=${expires}`;
  document.cookie = `googtrans=${cookie};domain=${host};path=/;expires=${expires}`;
  document.cookie = `googtrans=${cookie};domain=.${host.replace(/^www\./, "")};path=/;expires=${expires}`;
  // Persist user choice
  try { localStorage.setItem("cv_lang", code); } catch { /* noop */ }
  // Reload to apply (Google Translate reads cookie on page load)
  window.location.reload();
}

const LanguageSwitcher = () => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("es");
  const ref = useRef(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("cv_lang");
      if (saved && LANGS.some((l) => l.code === saved)) setCurrent(saved);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const active = LANGS.find((l) => l.code === current) || LANGS[0];

  return (
    <div ref={ref} className="relative notranslate" translate="no">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="language-switcher-btn"
        aria-label="Cambiar idioma"
        className="flex items-center gap-1.5 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors"
      >
        <Globe className="h-3.5 w-3.5" />
        <span className="text-base leading-none">{active.flag}</span>
        <span className="uppercase tracking-wider hidden sm:inline">{active.code}</span>
      </button>

      {open && (
        <div
          data-testid="language-switcher-menu"
          className="absolute right-0 mt-2 w-44 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg overflow-hidden z-50"
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => applyLanguage(l.code)}
              data-testid={`language-option-${l.code}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors ${
                current === l.code
                  ? "bg-[#45A0EE]/10 text-[#1D1D1B] dark:text-white"
                  : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
              }`}
            >
              <span className="text-lg leading-none">{l.flag}</span>
              <span className="flex-1 text-left">{l.label}</span>
              {current === l.code && <Check className="h-3.5 w-3.5 text-[#45A0EE]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
